/**
 * trigger-version-history-modal.tsx — Shows chronological change log for a trigger.
 *
 * AC 3: lists all changes chronologically with parameter name, old value, new value,
 * author, timestamp. No version can be deleted.
 */

import { Table } from '@nrx/ui';
import type { JSX } from 'react';

import type { EscalationParamVersion } from './escalation-params-api.js';

interface TriggerVersionHistoryModalProps {
  open: boolean;
  triggerName: string;
  history: EscalationParamVersion[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

export function TriggerVersionHistoryModal({
  open,
  triggerName,
  history,
  loading,
  error,
  onClose,
}: TriggerVersionHistoryModalProps): JSX.Element | null {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="version-history-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 id="version-history-dialog-title" className="text-lg font-semibold text-gray-900">
            Version History: {triggerName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close version history"
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        {!error && !loading && history.length === 0 && (
          <p className="text-gray-500 text-sm">No version history found.</p>
        )}

        {!error && (loading || history.length > 0) && (
          <Table.ScrollArea loading={loading} loadingLabel="Loading history…">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Parameter</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Old Value</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>New Value</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Author</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Timestamp</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {history.map((entry) => (
                  <Table.Row key={entry.id}>
                    <Table.Cell className="font-mono text-xs">{entry.parameterName}</Table.Cell>
                    <Table.Cell className="max-w-xs truncate font-mono text-xs">
                      {entry.oldValue === null ? '—' : JSON.stringify(entry.oldValue)}
                    </Table.Cell>
                    <Table.Cell className="max-w-xs truncate font-mono text-xs">
                      {JSON.stringify(entry.newValue)}
                    </Table.Cell>
                    <Table.Cell className="text-xs">{entry.author}</Table.Cell>
                    <Table.Cell className="whitespace-nowrap text-xs">
                      {new Date(entry.timestamp).toLocaleString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        )}
      </div>
    </div>
  );
}
